import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatPrice, formatApiError } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Checkout() {
  const { cart, cartTotal, refresh } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: user?.name || "", phone: "", line1: "", line2: "",
    city: "", state: "", pincode: "", country: "India",
  });
  const [payment, setPayment] = useState(form.country === "India" ? "razorpay" : "razorpay");

  if (!user) return <div className="ja-container py-32 text-center"><Link to="/login" className="ja-btn">Sign in to checkout</Link></div>;
  if (cart.length === 0) return <div className="ja-container py-32 text-center"><Link to="/shop" className="ja-btn">Your bag is empty</Link></div>;

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
const loadRazorpay = () => {
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};
  const submit = async (e) => {
  e.preventDefault();
  setSubmitting(true);

  try {
    const pm = form.country === "India" ? "razorpay" : "razorpay";

    if (pm === "razorpay") {
      const loaded = await loadRazorpay();
      if (!loaded) {
        toast.error("Razorpay SDK failed to load");
        setSubmitting(false);
        return;
      }

      const { data: rz } = await api.post("/payments/razorpay/create-order");

      const options = {
        key: rz.key,
        amount: rz.amount,
        currency: "INR",
        name: "Jewels Ashu",
        description: "Jewellery Order",
        order_id: rz.order_id,
        handler: async function (response) {
          const { data } = await api.post("/orders/checkout", {
            address: form,
            payment_method: "razorpay",
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });

          toast.success("Payment successful. Order placed.");
          await refresh();
          nav(`/order/success/${data.id}`);
        },
        prefill: {
          name: form.full_name,
          contact: form.phone,
          email: user?.email || "",
        },
        theme: {
          color: "#111111",
        },
        modal: {
          ondismiss: function () {
            setSubmitting(false);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
      return;
    }

    const { data } = await api.post("/orders/checkout", {
      address: form,
      payment_method: pm,
    });

    toast.success("Order placed");
    await refresh();
    nav(`/order/success/${data.id}`);
  } catch (e2) {
    toast.error(formatApiError(e2.response?.data?.detail) || "Checkout failed");
  } finally {
    setSubmitting(false);
  }
};

  return (
    <div className="ja-container py-16 md:py-24" data-testid="checkout-page">
      <div className="text-center mb-16">
        <p className="ja-overline">Finalise Your Order</p>
        <h1 className="ja-serif text-5xl md:text-6xl mt-3 tracking-tight">Checkout</h1>
      </div>
      <form onSubmit={submit} className="grid md:grid-cols-12 gap-12">
        <div className="md:col-span-7 space-y-8">
          <section>
            <p className="ja-overline mb-6">Shipping Address</p>
            <div className="grid sm:grid-cols-2 gap-6">
              <input required placeholder="Full Name" className="ja-input" value={form.full_name} onChange={onChange("full_name")} data-testid="ck-name" />
              <input required placeholder="Phone" className="ja-input" value={form.phone} onChange={onChange("phone")} data-testid="ck-phone" />
              <input required placeholder="Address Line 1" className="ja-input sm:col-span-2" value={form.line1} onChange={onChange("line1")} data-testid="ck-line1" />
              <input placeholder="Address Line 2 (optional)" className="ja-input sm:col-span-2" value={form.line2} onChange={onChange("line2")} data-testid="ck-line2" />
              <input required placeholder="City" className="ja-input" value={form.city} onChange={onChange("city")} data-testid="ck-city" />
              <input required placeholder="State" className="ja-input" value={form.state} onChange={onChange("state")} data-testid="ck-state" />
              <input required placeholder="Postal Code" className="ja-input" value={form.pincode} onChange={onChange("pincode")} data-testid="ck-pincode" />
              <select className="ja-input" value={form.country} onChange={onChange("country")} data-testid="ck-country">
                <option>India</option><option>United States</option><option>United Kingdom</option><option>Singapore</option><option>UAE</option><option>other</option>
              </select>
            </div>
          </section>
          <section className="pt-4">
            <p className="ja-overline mb-6">Payment Method</p>
            <div className="border p-5 text-sm" style={{ borderColor: "var(--ja-border)", background: "var(--ja-ivory)" }} data-testid="payment-method">
              <p><strong>{form.country === "India" ? "Razorpay" : "Razorpay"}</strong> will be used for {form.country}.</p>
              <p className="mt-2 text-xs tracking-[0.2em] uppercase" style={{ color: "var(--ja-gold)" }}>Thank you for choosing jewelsashu .</p>
            </div>
          </section>
        </div>
        <aside className="md:col-span-5">
          <div className="border p-8" style={{ borderColor: "var(--ja-border)", background: "var(--ja-ivory)" }}>
            <p className="ja-overline mb-6">Your Order</p>
            <div className="space-y-4 max-h-72 overflow-auto pr-2">
              {cart.map((it) => (
                <div key={it.product.id} className="flex gap-3 items-start text-sm">
                  <img src={it.product.images?.[0]} alt="" className="w-14 h-14 object-cover" />
                  <div className="flex-1">
                    <p className="ja-serif">{it.product.name}</p>
                    <p className="text-xs" style={{ color: "var(--ja-muted)" }}>× {it.quantity}</p>
                  </div>
                  <p>{formatPrice(it.product.price * it.quantity, it.product.currency)}</p>
                </div>
              ))}
            </div>
            <div className="border-t mt-6 pt-4 space-y-2 text-sm" style={{ borderColor: "var(--ja-border)" }}>
              <div className="flex justify-between"><span>Subtotal</span><span>{formatPrice(cartTotal)}</span></div>
              <div className="flex justify-between"><span>Shipping</span><span>Free</span></div>
              <div className="flex justify-between pt-2 border-t mt-2" style={{ borderColor: "var(--ja-border)" }}><span className="ja-serif text-lg">Total</span><span className="ja-serif text-lg" data-testid="ck-total">{formatPrice(cartTotal)}</span></div>
            </div>
            <button type="submit" disabled={submitting} className="ja-btn w-full mt-8" data-testid="place-order-button">
              {submitting ? "Placing…" : "Place Order"}
            </button>
          </div>
        </aside>
      </form>
    </div>
  );
}
